import { useState, useEffect } from 'react'
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock'
import { appToast } from '../../../../../components/AppToast'
import { getMasterOptions } from '../../api/mastersApi'
import { regenerateRollNumbers } from '../../api/studentsApi'

const ORDER_OPTIONS = [
  { value: 'entry', label: 'Order of entry' },
  { value: 'id-card', label: 'Id Card No' },
  { value: 'alpha-boys', label: 'Alphabetically (Boys First)' },
  { value: 'alpha-girls', label: 'Alphabetically (Girls First)' },
  { value: 'alpha-mixed', label: 'Alphabetically (Mixed Gender)' },
]

const GROUP_OPTIONS = [
  { value: 'batch', label: 'Batch' },
  { value: 'course', label: 'Course' },
  { value: 'branch', label: 'Branch' },
  { value: 'academic-year', label: 'Academic Year' },
  { value: 'continuous', label: 'Continuous' },
]

const NAME_ORDER_OPTIONS = [
  { value: 'first-name', label: 'First Name' },
  { value: 'last-name', label: 'Last Name' },
]

const START_FROM_OPTIONS = Array.from({ length: 50 }, (_, i) => String(i + 1))

function toOptions(arr) {
  return arr.map((item) =>
    typeof item === 'object' ? item : { value: item, label: item },
  )
}

export default function RegenerateRollNoModal({ isOpen, onClose, students = [], onComplete }) {
  const [company, setCompany] = useState('All')
  const [branch, setBranch] = useState('All')
  const [course, setCourse] = useState('All')
  const [scheme, setScheme] = useState('All')
  const [academicYear, setAcademicYear] = useState('All')
  const [batch, setBatch] = useState('All')
  const [startFrom, setStartFrom] = useState('1')
  const [enterRollNo, setEnterRollNo] = useState('1')
  const [orderBy, setOrderBy] = useState('alpha-mixed')
  const [groupBy, setGroupBy] = useState('batch')
  const [nameOrder, setNameOrder] = useState('first-name')
  const [excludeInactive, setExcludeInactive] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [masters, setMasters] = useState({})

  useEffect(() => {
    if (!isOpen) return
    let active = true
    const keys = ['institutes', 'branches', 'courses', 'schemes', 'academic_years', 'batches']
    Promise.all(keys.map((k) => getMasterOptions(k))).then((vals) => {
      if (!active) return
      const m = {}
      keys.forEach((k, i) => { m[k] = vals[i] || [] })
      setMasters(m)
    }).catch(() => {})
    return () => { active = false }
  }, [isOpen])

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleRegenerate = async () => {
    setIsProcessing(true)
    try {
      const studentIds = students.map((s) => s.id)
      const res = await regenerateRollNumbers({
        studentIds,
        enterRollNo: parseInt(enterRollNo, 10) || 1,
        orderBy,
        groupBy,
        nameOrder,
        excludeInactive,
        company,
        branch,
        course,
        scheme,
        academicYear,
        batch,
      })
      appToast.success(res.message || 'Roll numbers regenerated successfully.')
      onComplete?.()
      onClose()
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to regenerate roll numbers.')
    } finally {
      setIsProcessing(false)
    }
  }

  if (!isOpen) return null

  const companyOpts = toOptions(['All', ...(masters.institutes || [])])
  const branchOpts = toOptions(['All', ...(masters.branches || [])])
  const courseOpts = toOptions(['All', ...(masters.courses || [])])
  const schemeOpts = toOptions(['All', ...(masters.schemes || [])])
  const academicYearOpts = toOptions(['All', ...(masters.academic_years || [])])
  const batchOpts = toOptions(['All', ...(masters.batches || [])])
  const startFromOpts = START_FROM_OPTIONS.map((v) => ({ value: v, label: v }))

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] max-h-[820px] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Blue Banner Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between bg-primary px-8 py-5">
          <div>
            <h2 className="text-xl font-extrabold text-on-primary tracking-tight">Regenerate Roll No</h2>
            <p className="mt-0.5 text-sm text-on-primary/80">
              Recalculate roll numbers for students based on sorting criteria
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-on-primary/15 px-3 py-1 text-xs font-bold text-on-primary">
              {students.length} student{students.length !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-on-primary/70 transition-colors hover:bg-on-primary/10 hover:text-on-primary"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>
        </div>

        {/* ── Form Body ──────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          {/* Filter Scope Grid */}
          <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low p-5">
            <h4 className="mb-4 text-xs font-extrabold uppercase tracking-wider text-primary">
              1. Filter Scope (Optional)
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Company / Institute</label>
                <select
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {companyOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Branch</label>
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {branchOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Course</label>
                <select
                  value={course}
                  onChange={(e) => setCourse(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {courseOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Batch</label>
                <select
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {batchOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Scheme</label>
                <select
                  value={scheme}
                  onChange={(e) => setScheme(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {schemeOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Academic Year</label>
                <select
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {academicYearOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Roll No Calculation Settings */}
          <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low p-5">
            <h4 className="mb-4 text-xs font-extrabold uppercase tracking-wider text-primary">
              2. Numbering & Sorting Logic
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Start From (Index)</label>
                <select
                  value={startFrom}
                  onChange={(e) => setStartFrom(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {startFromOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Starting Roll No</label>
                <input
                  type="number"
                  min="1"
                  value={enterRollNo}
                  onChange={(e) => setEnterRollNo(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Ordering Criteria</label>
                <select
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {ORDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Grouping Strategy</label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-on-surface-variant">Name Order</label>
                <select
                  value={nameOrder}
                  onChange={(e) => setNameOrder(e.target.value)}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface shadow-xs"
                >
                  {NAME_ORDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={excludeInactive}
                    onChange={(e) => setExcludeInactive(e.target.checked)}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20"
                  />
                  <span className="text-xs font-bold text-on-surface">Exclude Inactive / Archived</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* ── Modal Footer ────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 px-8 py-4 bg-surface-container-lowest">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/30 px-5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={isProcessing}
            className="rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:opacity-95 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                Regenerating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">sync</span>
                Regenerate Roll Numbers
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
